import { FormEvent } from "react"
import { useForm, Link } from "@inertiajs/react"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { t } from "@/lib/i18n"
import { TimeZoneOption, browserTimeZone } from "@/lib/time-zone"

export type AdminLocationFormValue = {
  id?: number | null
  name: string
  status: string
  timeZone?: string | null
  pickupDinerFeePercentage: string
  pickupRestaurantFeePercentage: string
}

export type StatusOption = {
  value: string
  label: string
}

type AdminLocationFormData = {
  name: string
  status: string
  time_zone: string
  pickup_diner_fee_percentage: string
  pickup_restaurant_fee_percentage: string
}

type ServerErrors = Partial<Record<keyof AdminLocationFormData, string | string[]>>

type Props = {
  location: AdminLocationFormValue
  statusOptions: StatusOption[]
  timezones: TimeZoneOption[]
  action: string
  method: "post" | "patch"
  title: string
  errors?: ServerErrors
}

function errorMessage(value: string | string[] | undefined): string | null {
  if (!value) return null

  return Array.isArray(value) ? value[0] : value
}

export function AdminLocationForm({
  location,
  statusOptions,
  timezones,
  action,
  method,
  title,
  errors: serverErrors = {},
}: Props) {
  const {
    data,
    setData,
    post,
    patch,
    transform,
    processing,
    errors: formErrors,
  } = useForm<AdminLocationFormData>({
    name: location.name || "",
    status: location.status || "inactive",
    time_zone: location.timeZone || browserTimeZone(),
    pickup_diner_fee_percentage: location.pickupDinerFeePercentage || "0.0",
    pickup_restaurant_fee_percentage: location.pickupRestaurantFeePercentage || "0.0",
  })

  const timeZoneOptions = !data.time_zone || timezones.some((option) => option.value === data.time_zone)
    ? timezones
    : [{ value: data.time_zone, label: data.time_zone }, ...timezones]

  const fieldError = (field: keyof AdminLocationFormData) => (
    errorMessage(serverErrors[field] || formErrors[field])
  )

  const submit = (event: FormEvent) => {
    event.preventDefault()
    transform((payload) => ({ location: payload }))

    if (method === "patch") {
      patch(action, { preserveScroll: true })
      return
    }

    post(action, { preserveScroll: true })
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={submit} className="space-y-6">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="name">{t("ui.react.pages.admin.locations.form.name")}</Label>
              <Input
                id="name"
                value={data.name}
                onChange={(event) => setData("name", event.target.value)}
                required
              />
              {fieldError("name") ? (
                <p className="text-sm font-medium text-destructive">{fieldError("name")}</p>
              ) : null}
            </div>

            <div className="space-y-2">
              <Label htmlFor="status">{t("ui.react.pages.admin.locations.form.status")}</Label>
              <Select value={data.status} onValueChange={(value) => setData("status", value)}>
                <SelectTrigger id="status" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {statusOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {fieldError("status") ? (
                <p className="text-sm font-medium text-destructive">{fieldError("status")}</p>
              ) : null}
            </div>

            <div className="space-y-2">
              <Label htmlFor="time_zone">{t("ui.react.pages.admin.locations.form.time_zone")}</Label>
              <Select value={data.time_zone} onValueChange={(value) => setData("time_zone", value)}>
                <SelectTrigger id="time_zone" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {timeZoneOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {fieldError("time_zone") ? (
                <p className="text-sm font-medium text-destructive">{fieldError("time_zone")}</p>
              ) : null}
            </div>

            <div className="space-y-2">
              <Label htmlFor="pickup_diner_fee_percentage">
                {t("ui.react.pages.admin.locations.form.pickup_diner_fee_percentage")}
              </Label>
              <Input
                id="pickup_diner_fee_percentage"
                type="number"
                min="0"
                max="100"
                step="0.01"
                value={data.pickup_diner_fee_percentage}
                onChange={(event) => setData("pickup_diner_fee_percentage", event.target.value)}
                required
              />
              {fieldError("pickup_diner_fee_percentage") ? (
                <p className="text-sm font-medium text-destructive">
                  {fieldError("pickup_diner_fee_percentage")}
                </p>
              ) : null}
            </div>

            <div className="space-y-2">
              <Label htmlFor="pickup_restaurant_fee_percentage">
                {t("ui.react.pages.admin.locations.form.pickup_restaurant_fee_percentage")}
              </Label>
              <Input
                id="pickup_restaurant_fee_percentage"
                type="number"
                min="0"
                max="100"
                step="0.01"
                value={data.pickup_restaurant_fee_percentage}
                onChange={(event) => setData("pickup_restaurant_fee_percentage", event.target.value)}
                required
              />
              {fieldError("pickup_restaurant_fee_percentage") ? (
                <p className="text-sm font-medium text-destructive">
                  {fieldError("pickup_restaurant_fee_percentage")}
                </p>
              ) : null}
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button type="submit" disabled={processing}>
              {t("ui.react.pages.admin.locations.form.save")}
            </Button>
            <Button asChild type="button" variant="outline">
              <Link href="/locations">{t("ui.react.pages.admin.locations.form.cancel")}</Link>
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  )
}
